/**
 * One imported file: what it was, and what became of every row - searchable,
 * filterable, a page at a time - with what can be done with it: export its
 * moulds or its rows, download its error report, import it again, and delete
 * its moulds or its place in Import History.
 *
 * Deleting is the catalog's own delete, and only ever of a mould in this
 * import. The ones it added go with one confirmation. Any other - one it only
 * filled in, which an earlier import or a person added - only when that is
 * ticked on purpose too, and the dialog says where each came from. The server
 * decides which is which (ImportRecordDto.deletable, origin) and checks again
 * when asked, so the screen cannot talk it into more.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileSpreadsheet,
  History,
  Info,
  RefreshCw,
  Search,
  Trash2,
  TriangleAlert,
  Upload,
} from 'lucide-react';
import type { ImportDeleteRequest, ImportRecordDto, ImportRecordOrigin, ImportRecordsResponse } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Dialog } from '../../components/ui/dialog.js';
import { Input, Select } from '../../components/ui/field.js';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../../components/ui/menu.js';
import { Badge, Card, Code, EmptyState } from '../../components/ui/primitives.js';
import { deleteImportData, getImportRecords, isNetworkError } from '../../api/client.js';
import { useCan } from '../../auth/AuthProvider.js';
import { useMoulds } from '../../db/queries.js';
import { formatDateTime } from '../../lib/format.js';
import { syncNow } from '../../sync/engine.js';
import { DEFAULT_EXPORT_FORMAT, exportMouldsToExcel } from '../moulds/exportMoulds.js';
import { MouldImportDialog } from '../moulds/MouldImportDialog.js';
import { useMouldLocations } from '../moulds/useMouldLocations.js';
import { RECORD_ACTION, RUN_STATUS, baseName, downloadCsv, fileType, runTotals } from './importUi.js';

const PAGE_SIZE = 25;

type FilterKey = 'all' | 'create' | 'restore' | 'update' | 'unchanged' | 'blocked' | 'photo' | 'deletable' | 'deleted';
const FILTERS: Array<{ key: FilterKey; label: string; test: (r: ImportRecordDto) => boolean }> = [
  { key: 'all', label: 'All records', test: () => true },
  { key: 'create', label: 'New', test: (r) => r.action === 'create' },
  { key: 'restore', label: 'Brought back', test: (r) => r.action === 'restore' },
  { key: 'update', label: 'Filled in', test: (r) => r.action === 'update' },
  { key: 'unchanged', label: 'Already in', test: (r) => r.action === 'unchanged' },
  { key: 'blocked', label: 'Not imported', test: (r) => r.action === 'blocked' },
  { key: 'photo', label: 'Photo problems', test: (r) => !!r.photoProblem },
  { key: 'deletable', label: 'Added by this import', test: (r) => r.deletable },
  { key: 'deleted', label: 'Deleted since', test: (r) => !!r.mould?.deleted },
];

/** selected: the ticked ones (or one record); added: every mould it added; every: every mould in it. */
type DeleteMode = 'selected' | 'added' | 'every' | 'history';
type Deleting = { mode: DeleteMode; targets: ImportRecordDto[]; removeToo: boolean };
type Notice = { tone: 'success' | 'warning' | 'danger'; text: ReactNode };

/** A mould of this import that is in the catalog: it can be selected and deleted from here. */
const selectable = (r: ImportRecordDto) => !!r.mould && !r.mould.deleted;

/** One record per mould. */
const byMould = (list: ImportRecordDto[]) => [...new Map(list.filter((r) => r.mould).map((r) => [r.mould!.id, r])).values()];

export function ImportDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const canDelete = useCan('delete', 'moulds');
  const canExport = useCan('export', 'moulds');
  const canImport = useCan('create', 'moulds');

  const [data, setData] = useState<ImportRecordsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [page, setPage] = useState(1);
  // Record ids - the table's row keys.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openRecord, setOpenRecord] = useState<ImportRecordDto | null>(null);
  const [deleting, setDeleting] = useState<Deleting | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // For the Excel export: the moulds as this device holds them, with names resolved.
  const catalog = useMoulds({});
  const places = useMouldLocations();

  // Another import (a link from a record's origin): its own selection and search.
  useEffect(() => {
    setSelected(new Set());
    setSearch('');
    setFilter('all');
    setOpenRecord(null);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getImportRecords(id)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
        // A selection only ever holds moulds that can still be deleted.
        const still = new Set(r.records.filter(selectable).map((x) => x.id));
        setSelected((s) => new Set([...s].filter((k) => still.has(k))));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { status?: number })?.status;
        setError(
          isNetworkError(e)
            ? 'No connection. Import history is kept on the server.'
            : status === 404
              ? 'This import is not in Import History - it may have been removed.'
              : 'Could not load this import.',
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, reload]);

  const records = useMemo(() => data?.records ?? [], [data]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, records.filter(f.test).length])) as Record<FilterKey, number>, [records]);
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const test = FILTERS.find((f) => f.key === filter)!.test;
    return records.filter(
      (r) =>
        test(r) &&
        (!needle ||
          [r.assetCode, r.name, r.reason, r.mould?.name, r.sheetRow != null ? String(r.sheetRow) : '']
            .some((v) => (v ?? '').toLowerCase().includes(needle))),
    );
  }, [records, search, filter]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const pageRows = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => setPage(1), [search, filter]);
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  const deletable = useMemo(() => byMould(records.filter((r) => r.deletable)), [records]);
  const inCatalog = useMemo(() => byMould(records.filter(selectable)), [records]);
  const selectedRecords = useMemo(() => byMould(records.filter((r) => selected.has(r.id) && selectable(r))), [records, selected]);
  const problems = data?.run.problems ?? [];

  if (!data) {
    return (
      <Page title="Import" back={{ to: '/imports', label: 'Import history' }}>
        {error ? (
          <Card>
            <EmptyState icon={<FileSpreadsheet />} title={error} description={<Link to="/imports" className="text-primary hover:underline">Back to Import history</Link>} />
          </Card>
        ) : (
          <p className="text-sm text-muted-foreground">{loading ? 'Loading…' : ''}</p>
        )}
      </Page>
    );
  }

  const { run, source } = data;
  const totals = runTotals(run);
  const status = RUN_STATUS[run.status];

  // --- downloads ---------------------------------------------------------------
  async function exportMoulds() {
    const ids = new Set(inCatalog.map((r) => r.mould!.id));
    const rows = (catalog ?? []).filter((m) => ids.has(m.id));
    if (!rows.length) {
      setNotice({ tone: 'warning', text: 'None of this import\'s moulds is on this device yet. Wait for it to sync, then try again.' });
      return;
    }
    setExporting(true);
    setNotice(null);
    try {
      const res = await exportMouldsToExcel({ rows, places, format: DEFAULT_EXPORT_FORMAT, label: `from ${baseName(run.fileName)}` });
      setNotice({
        tone: res.missed.length ? 'warning' : 'success',
        text: `Exported ${res.moulds} mould${res.moulds === 1 ? '' : 's'} with ${res.photos} photo${res.photos === 1 ? '' : 's'}${res.missed.length ? ` - ${res.missed.length} photo${res.missed.length === 1 ? '' : 's'} could not be included` : ''}.`,
      });
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'The export could not be created.' });
    } finally {
      setExporting(false);
    }
  }

  function downloadRecords() {
    downloadCsv(`${baseName(run.fileName)} - records.csv`, [
      ['Sheet', 'Row', 'Mould code', 'Mould name', 'Result', 'Fields written', 'Values kept (typed by hand)', 'Why not imported', 'Photos', 'Photo problem', 'Placed at', 'In the catalog now'],
      ...records.map((r) => [
        r.sheet,
        r.sheetRow,
        r.assetCode,
        r.name,
        RECORD_ACTION[r.action].label,
        r.writes.join('; '),
        r.kept.map((k) => `${k.field}: kept "${k.catalog}", sheet "${k.sheet}"`).join('; '),
        r.reason,
        r.photoCount,
        r.photoProblem,
        r.placeAt,
        r.mould ? (r.mould.deleted ? 'Deleted' : 'Yes') : '',
      ]),
    ]);
  }

  function downloadErrors() {
    downloadCsv(`${baseName(run.fileName)} - error report.csv`, [
      ['Sheet', 'Row', 'Mould code', 'What did not go in', 'Why'],
      ...(run.error ? [['', '', '', 'The whole import', run.error]] : []),
      ...problems.map((p) => [p.sheet, p.row, p.code, p.photo ? 'A photo' : 'The row', p.reason]),
    ]);
  }

  // --- deleting ------------------------------------------------------------------
  const openDelete = (mode: DeleteMode, removeToo = false) =>
    setDeleting({
      mode,
      removeToo,
      targets: mode === 'selected' ? selectedRecords : mode === 'added' ? deletable : mode === 'every' ? inCatalog : [],
    });
  const notOwn = selectedRecords.filter((r) => !r.deletable).length;

  // --- the table -----------------------------------------------------------------
  const pageSelectable = pageRows.filter(selectable).map((r) => r.id);
  const allOnPage = pageSelectable.length > 0 && pageSelectable.every((k) => selected.has(k));

  const columns: Array<Column<ImportRecordDto>> = [
    {
      key: 'row',
      header: 'Row',
      headerClassName: 'w-20',
      hideOnMobile: true,
      render: (r) =>
        r.sheetRow != null ? (
          <span className="tabular">
            {r.sheetRow}
            {run.sheetNames.length > 1 && r.sheet ? <span className="block text-xs text-subtle-foreground">{r.sheet}</span> : null}
          </span>
        ) : (
          <span className="text-subtle-foreground">-</span>
        ),
    },
    {
      key: 'mould',
      header: 'Mould',
      primary: true,
      headerClassName: 'min-w-44',
      className: 'min-w-44 max-w-xs',
      render: (r) => (
        <span className="block min-w-0">
          <Code className="font-semibold">{r.assetCode || '(no number)'}</Code>
          <span className="block truncate text-xs text-muted-foreground" title={r.name || r.mould?.name || ''}>
            {r.name || r.mould?.name || ''}
          </span>
        </span>
      ),
    },
    {
      key: 'result',
      header: 'Result',
      className: 'whitespace-nowrap',
      render: (r) => <Badge tone={RECORD_ACTION[r.action].tone}>{RECORD_ACTION[r.action].label}</Badge>,
    },
    {
      key: 'detail',
      header: 'What happened',
      headerClassName: 'min-w-52',
      className: 'min-w-52 max-w-sm',
      hideOnMobile: true,
      render: (r) => (
        <span className="block text-sm">
          {r.action === 'blocked' ? (
            <span className="text-danger">{r.reason}</span>
          ) : r.writes.length ? (
            <span className="line-clamp-2 text-muted-foreground" title={r.writes.join(', ')}>
              Wrote {r.writes.join(', ')}
            </span>
          ) : (
            <span className="text-subtle-foreground">Nothing new</span>
          )}
          {r.kept.length ? (
            <span className="block text-xs text-warning">
              {r.kept.length} value{r.kept.length === 1 ? '' : 's'} kept - typed by hand
            </span>
          ) : null}
          {r.photoProblem ? <span className="block text-xs text-danger">Photo: {r.photoProblem}</span> : null}
        </span>
      ),
    },
    {
      key: 'photos',
      header: 'Photos',
      hideOnMobile: true,
      render: (r) => <span className="tabular">{r.photoCount || <span className="text-subtle-foreground">-</span>}</span>,
    },
    {
      key: 'now',
      header: 'In the catalog now',
      render: (r) =>
        !r.mould ? (
          <span className="text-subtle-foreground">-</span>
        ) : r.mould.deleted ? (
          <Badge tone="danger">Deleted</Badge>
        ) : (
          <span className="whitespace-nowrap">
            <Badge tone="success">In catalog</Badge>
            {r.origin ? (
              <span className="mt-0.5 block max-w-44 truncate text-xs text-subtle-foreground" title={originShort(r.origin)}>
                {originShort(r.origin)}
              </span>
            ) : null}
            {r.changedSince ? <span className="mt-0.5 block text-xs text-subtle-foreground">changed since</span> : null}
          </span>
        ),
    },
  ];

  return (
    <Page
      title={run.fileName}
      back={{ to: '/imports', label: 'Import history' }}
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge tone={status.tone}>{status.label}</Badge>
          <span>
            {fileType(run.fileName)} · imported {formatDateTime(run.startedAt)} by {run.userName ?? 'unknown'}
            {run.sheetNames.length ? ` · ${run.sheetNames.length === 1 ? 'sheet' : 'sheets'} ${run.sheetNames.join(', ')}` : ''}
            {run.locationName ? ` · location ${run.locationName}` : ''}
          </span>
        </span>
      }
      actions={
        <>
          <Button variant="secondary" className="hidden sm:inline-flex" onClick={() => setReload((n) => n + 1)} loading={loading} aria-label="Refresh">
            <RefreshCw className="size-4" />
          </Button>
          {canImport ? (
            <Button
              variant="secondary"
              aria-label="Import again"
              onClick={() => setImportOpen(true)}
              title="Choose the file again - MouldTrack does not keep the spreadsheet itself. Rows already in are left as they are."
            >
              <Upload className="size-4" /> <span className="hidden sm:inline">Import again</span>
            </Button>
          ) : null}
          <Menu
            trigger={
              <Button variant="secondary" loading={exporting} aria-label="Download">
                <Download className="size-4" /> <span className="hidden sm:inline">Download</span>
              </Button>
            }
          >
            <MenuLabel>From this import</MenuLabel>
            {canExport ? (
              <MenuItem icon={<FileSpreadsheet />} disabled={!inCatalog.length} onSelect={() => void exportMoulds()}>
                Its moulds - Excel, Master Sheet format ({inCatalog.length})
              </MenuItem>
            ) : null}
            <MenuItem icon={<Download />} disabled={!records.length} onSelect={downloadRecords}>
              Every record - CSV ({records.length})
            </MenuItem>
            <MenuItem icon={<TriangleAlert />} disabled={!problems.length && !run.error} onSelect={downloadErrors}>
              Error report - CSV ({problems.length + (run.error ? 1 : 0)})
            </MenuItem>
          </Menu>
          {canDelete ? (
            <Menu
              trigger={
                <Button variant="danger">
                  <Trash2 className="size-4" /> Delete
                </Button>
              }
            >
              <MenuLabel>Moulds in this import</MenuLabel>
              <MenuItem destructive disabled={!selectedRecords.length} onSelect={() => openDelete('selected')}>
                Delete the selected moulds ({selectedRecords.length})
              </MenuItem>
              <MenuItem destructive disabled={!deletable.length} onSelect={() => openDelete('added')}>
                Delete every mould it added ({deletable.length})
              </MenuItem>
              {inCatalog.length > deletable.length ? (
                <MenuItem destructive onSelect={() => openDelete('every')}>
                  Delete every mould in this import ({inCatalog.length})
                </MenuItem>
              ) : null}
              <MenuSeparator />
              <MenuLabel>Import History</MenuLabel>
              <MenuItem icon={<History />} onSelect={() => openDelete('history')}>
                Remove from Import History only
              </MenuItem>
              <MenuItem destructive disabled={!inCatalog.length} onSelect={() => openDelete('every', true)}>
                Delete all its moulds and remove it ({inCatalog.length})
              </MenuItem>
            </Menu>
          ) : null}
        </>
      }
    >
      {notice ? (
        <p
          role="status"
          className={`mb-3 rounded-lg px-3 py-2 text-sm ${
            notice.tone === 'success' ? 'bg-success-subtle text-success' : notice.tone === 'warning' ? 'bg-warning-subtle text-warning' : 'bg-danger-subtle text-danger'
          }`}
        >
          {notice.text}
        </p>
      ) : null}

      {run.status === 'FAILED' ? (
        <Banner tone="danger" icon={<TriangleAlert />}>
          <b>This import failed, and nothing was written.</b> {run.error} Choose <b>Import again</b> to try once more.
        </Banner>
      ) : run.status === 'RUNNING' ? (
        <Banner tone="info" icon={<Info />}>
          This import is still in progress. Refresh in a moment.
        </Banner>
      ) : null}
      {source === 'rebuilt' ? (
        <Banner tone="info" icon={<Info />}>
          <b>An import from before 28 Oct 2026, when rows were not kept.</b> Its moulds are traced through the activity
          log: rows that changed nothing are not listed, and the sheet row is known only for the ones that did not go in.
        </Banner>
      ) : null}

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        <Stat label="Records" value={run.rowsTotal} />
        <Stat label="New" value={run.created} tone="success" />
        <Stat label="Brought back" value={run.restored} />
        <Stat label="Filled in" value={run.updated} />
        <Stat label="Already in" value={run.unchanged} />
        <Stat label="Not imported" value={totals.notImported} tone={totals.notImported ? 'danger' : undefined} />
        <Stat label="Photos added" value={run.photosAdded} hint={run.photosFailed ? `${run.photosFailed} failed` : undefined} />
        <Stat label="Deleted since" value={run.mouldsDeleted ?? 0} hint="from this page" />
      </div>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground" aria-hidden />
          <Input aria-label="Search records" placeholder="Search by code, name, row or reason…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select
          aria-label="Show"
          value={filter}
          onChange={(e) => setFilter(e.target.value as FilterKey)}
          options={FILTERS.filter((f) => f.key === 'all' || counts[f.key] > 0).map((f) => ({ value: f.key, label: `${f.label} (${counts[f.key]})` }))}
          className="h-9.5 sm:w-64"
        />
      </div>

      {/* Phones: the table's own select-all is in its heading, which they do not have. */}
      {canDelete && pageSelectable.length ? (
        <label className="mb-2 flex w-fit items-center gap-2 text-sm text-muted-foreground lg:hidden">
          <input
            type="checkbox"
            className="size-4"
            checked={allOnPage}
            onChange={() =>
              setSelected((sel) => {
                const next = new Set(sel);
                for (const k of pageSelectable) (allOnPage ? next.delete(k) : next.add(k));
                return next;
              })
            }
          />
          Select the {pageSelectable.length} mould{pageSelectable.length === 1 ? '' : 's'} on this page
        </label>
      ) : null}

      {canDelete && selectedRecords.length ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-primary-subtle px-3 py-2 text-sm">
          <span>
            <b>{selectedRecords.length}</b> mould{selectedRecords.length === 1 ? '' : 's'} selected
            {notOwn ? <span className="text-muted-foreground"> - {notOwn} not added by this import</span> : null}
          </span>
          <Button variant="danger" size="sm" onClick={() => openDelete('selected')}>
            <Trash2 className="size-4" /> Delete selected
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}

      <Card className="overflow-hidden">
        <DataTable
          rows={pageRows}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={setOpenRecord}
          selection={canDelete ? { selected, onChange: setSelected, canSelect: selectable, label: (r) => `Select ${r.assetCode}` } : undefined}
          empty={
            <EmptyState
              icon={<FileSpreadsheet />}
              title={records.length ? 'No records match' : 'No records'}
              description={records.length ? 'Try another search, or show all records.' : run.status === 'FAILED' ? 'Nothing was written by this import.' : 'This import has no rows to show.'}
            />
          }
        />
      </Card>

      {shown.length > PAGE_SIZE ? (
        <nav className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm" aria-label="Pages">
          <span className="text-muted-foreground">
            {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, shown.length)} of {shown.length}
          </span>
          <span className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="size-4" /> Previous
            </Button>
            <span className="tabular text-muted-foreground">
              Page {page} of {pages}
            </span>
            <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Next <ChevronRight className="size-4" />
            </Button>
          </span>
        </nav>
      ) : null}

      <RecordDialog
        record={openRecord}
        onClose={() => setOpenRecord(null)}
        onDelete={
          canDelete
            ? (r) => {
                setOpenRecord(null);
                setDeleting({ mode: 'selected', targets: [r], removeToo: false });
              }
            : undefined
        }
      />

      {deleting ? (
        <DeleteDialog
          {...deleting}
          run={run.id}
          runName={run.fileName}
          inCatalog={inCatalog.length}
          onClose={() => setDeleting(null)}
          onDone={(text, historyRemoved, kept) => {
            setDeleting(null);
            // So this device's catalog drops the deleted moulds now, not at the next sync.
            void syncNow();
            if (historyRemoved) navigate('/imports', { state: { notice: text } });
            else {
              // The reload drops the deleted ones from the selection.
              setNotice({ tone: kept ? 'warning' : 'success', text });
              setReload((n) => n + 1);
            }
          }}
        />
      ) : null}

      <MouldImportDialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) setReload((n) => n + 1);
        }}
      />
    </Page>
  );
}

// --- pieces ---------------------------------------------------------------------

function Banner({ tone, icon, children }: { tone: 'info' | 'danger'; icon: ReactNode; children: ReactNode }) {
  return (
    <div
      className={`mb-3 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${
        tone === 'danger' ? 'border-danger/40 bg-danger-subtle text-danger' : 'border-info/25 bg-info-subtle text-info'
      }`}
    >
      <span className="mt-0.5 shrink-0 [&_svg]:size-4">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function Stat({ label, value, tone, hint }: { label: string; value: number; tone?: 'success' | 'danger'; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-3 py-2">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className={`block text-lg font-semibold tabular ${tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : ''}`}>{value}</span>
      {hint ? <span className="block text-xs text-subtle-foreground">{hint}</span> : null}
    </div>
  );
}

/** A mould's origin in a few words, under its "In catalog". */
const originShort = (o: ImportRecordOrigin) => (o.kind === 'import' ? `from ${o.fileName}` : 'added by hand');

/**
 * Where a mould this import did not add came from, as a sentence - for the
 * delete dialog, which groups the moulds by it.
 */
function originNote(r: ImportRecordDto): string {
  // Added by this import, but no longer only its own: the reason says why.
  if (r.action === 'create' || r.action === 'restore') return r.keepReason ?? 'No longer this import\'s own.';
  const o = r.origin;
  if (o?.kind === 'import') {
    return `Added by ${o.fileName}, imported ${formatDateTime(o.startedAt)}${o.inHistory ? '' : ' - since removed from Import History'}.`;
  }
  if (o?.kind === 'hand') return 'Added by hand.';
  return 'No record of where they came from.';
}

function Origin({ origin, onOpen }: { origin: ImportRecordOrigin | null; onOpen: () => void }) {
  if (origin?.kind === 'hand') return <>By hand</>;
  if (origin?.kind !== 'import') return <span className="text-muted-foreground">Not on record</span>;
  return (
    <>
      By{' '}
      {origin.inHistory ? (
        <Link to={`/imports/${origin.runId}`} onClick={onOpen} className="text-primary hover:underline">
          {origin.fileName}
        </Link>
      ) : (
        origin.fileName
      )}
      , imported {formatDateTime(origin.startedAt)}
      {origin.inHistory ? null : <span className="block text-muted-foreground">Since removed from Import History.</span>}
    </>
  );
}

/** Everything about one row: what the sheet said, what the import did, the mould now. */
function RecordDialog({
  record: r,
  onClose,
  onDelete,
}: {
  record: ImportRecordDto | null;
  onClose: () => void;
  onDelete?: (r: ImportRecordDto) => void;
}) {
  if (!r) return null;
  const action = RECORD_ACTION[r.action];
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Code className="font-semibold">{r.assetCode || '(no number)'}</Code>
          <Badge tone={action.tone}>{action.label}</Badge>
        </span>
      }
      description={action.meaning}
      footer={
        <>
          {r.mould && !r.mould.deleted ? (
            <Link to={`/moulds/${r.mould.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
              Open the mould <ExternalLink className="size-3.5" />
            </Link>
          ) : null}
          {onDelete && selectable(r) ? (
            <Button variant="danger" onClick={() => onDelete(r)}>
              <Trash2 className="size-4" /> Delete this mould
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-2 text-sm">
        <Fact label="Sheet and row">{r.sheetRow != null ? `${r.sheet ?? ''} · row ${r.sheetRow}` : 'Not kept for imports before 28 Oct 2026'}</Fact>
        <Fact label="Name on the sheet">{r.name || '-'}</Fact>
        {r.action === 'blocked' ? <Fact label="Why not imported"><span className="text-danger">{r.reason}</span></Fact> : null}
        {r.action !== 'blocked' ? <Fact label="Fields written">{r.writes.length ? r.writes.join(', ') : 'None - nothing new'}</Fact> : null}
        {r.kept.length ? (
          <Fact label="Values kept">
            <ul className="flex flex-col gap-1">
              {r.kept.map((k) => (
                <li key={k.field}>
                  <b>{k.field}</b>: kept “{k.catalog}”, typed by hand; the sheet said “{k.sheet}”
                </li>
              ))}
            </ul>
          </Fact>
        ) : null}
        <Fact label="Photos">{r.photoCount || 'None'}{r.photoProblem ? <span className="block text-danger">Not imported: {r.photoProblem}</span> : null}</Fact>
        {r.placeAt || r.placeNote ? <Fact label="Location">{r.placeAt ? `Placed at ${r.placeAt}` : ''}{r.placeNote ? <span className="block text-muted-foreground">{r.placeNote}</span> : null}</Fact> : null}
        <Fact label="In the catalog now">
          {!r.mould ? 'Not a mould - the row did not go in.' : r.mould.deleted ? 'Deleted' : `Yes - ${r.mould.name}`}
          {r.mould && r.changedSince ? <span className="block text-muted-foreground">Scanned, edited or changed by a later import since.</span> : null}
        </Fact>
        {r.mould && (r.action === 'update' || r.action === 'unchanged') ? (
          <Fact label="Added to the catalog">
            <Origin origin={r.origin} onOpen={onClose} />
          </Fact>
        ) : null}
        {r.mould ? (
          <Fact label="Delete from this import">
            {r.deletable ? (
              'Yes - this import added it.'
            ) : selectable(r) ? (
              <span className="text-muted-foreground">{r.keepReason} It can still be deleted from here, with a second confirmation.</span>
            ) : (
              <span className="text-muted-foreground">No. {r.keepReason}</span>
            )}
          </Fact>
        ) : null}
      </dl>
    </Dialog>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

/**
 * Says exactly what goes and what stays before anything is deleted - which
 * moulds, the history entry, or both. Moulds take a tick; any this import did
 * not add take a second one, with where each of them came from.
 */
function DeleteDialog({
  mode,
  run,
  runName,
  targets,
  inCatalog,
  removeToo,
  onClose,
  onDone,
}: {
  mode: DeleteMode;
  run: string;
  runName: string;
  /** The moulds to delete, as this screen has them - the server checks each again. */
  targets: ImportRecordDto[];
  /** How many of this import's moulds are in the catalog. */
  inCatalog: number;
  /** Start with "Also remove it from Import History" ticked. */
  removeToo: boolean;
  onClose: () => void;
  onDone: (text: string, historyRemoved: boolean, kept: number) => void;
}) {
  const [understood, setUnderstood] = useState(false);
  const [othersToo, setOthersToo] = useState(false);
  const [history, setHistory] = useState(mode === 'history' || removeToo);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const own = targets.filter((r) => r.deletable);
  const others = targets.filter((r) => !r.deletable);
  const n = targets.length;
  const changed = targets.filter((r) => r.changedSince).length;
  const whence = new Map<string, number>();
  for (const r of others) whence.set(originNote(r), (whence.get(originNote(r)) ?? 0) + 1);
  const codes = (list: ImportRecordDto[]) => (list.length <= 6 ? `: ${list.map((r) => r.mould?.assetCode ?? r.assetCode).join(', ')}` : '');
  const moulds = (k: number) => `${k} mould${k === 1 ? '' : 's'}`;

  const title = mode === 'history' ? 'Remove this import from Import History?' : `Delete ${moulds(n)}${history ? ' and remove the import' : ''}?`;

  async function confirm() {
    setBusy(true);
    setError(null);
    const body: ImportDeleteRequest = {
      // "Every mould" is left to the server, which picks them again as they are now.
      ...(n ? { moulds: mode === 'added' || mode === 'every' ? ('all' as const) : targets.map((r) => r.mould!.id) } : {}),
      ...(others.length ? { includeExisting: true } : {}),
      ...(history ? { history: true } : {}),
    };
    try {
      const res = await deleteImportData(run, body);
      const parts: string[] = [];
      if (res.mouldsDeleted) parts.push(`Deleted ${moulds(res.mouldsDeleted)} from the catalog`);
      if (res.historyRemoved) parts.push(`removed ${runName} from Import History`);
      let text = parts.length ? `${parts.join(' and ')}.` : 'Nothing was deleted.';
      if (res.notDeleted.length) text += ` ${res.notDeleted.length} kept: ${res.notDeleted.slice(0, 3).map((x) => `${x.assetCode || x.mouldId} (${x.reason})`).join('; ')}${res.notDeleted.length > 3 ? '…' : ''}`;
      onDone(text, res.historyRemoved, res.notDeleted.length);
    } catch (e) {
      setError(isNetworkError(e) ? 'No connection - nothing was deleted.' : e instanceof Error ? e.message : 'Nothing was deleted.');
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && !busy && onClose()}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void confirm()}
            loading={busy}
            disabled={(n > 0 && !understood) || (others.length > 0 && !othersToo) || (n === 0 && !history)}
          >
            {mode === 'history' ? 'Remove from history' : `Delete ${moulds(n)}${history ? ' and remove' : ''}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {n > 0 ? (
          <div>
            <p className="font-medium">What is deleted</p>
            <ul className="list-disc pl-5 text-muted-foreground">
              {own.length ? (
                <li>
                  {moulds(own.length)} this import added{codes(own)}
                </li>
              ) : null}
              {others.length ? (
                <li>
                  {moulds(others.length)} another import or a person added{codes(others)}
                  <ul className="mt-0.5 list-[circle] pl-5">
                    {[...whence].map(([note, count]) => (
                      <li key={note}>
                        <span className="tabular font-medium text-foreground">{count}</span> · {note}
                      </li>
                    ))}
                  </ul>
                </li>
              ) : null}
            </ul>
            <p className="mt-1 text-muted-foreground">
              Deleted from the catalog the same way as deleting them there: gone from the catalog and the phones, their
              history kept. Importing a file with them again brings them back.
            </p>
            {changed ? (
              <p className="mt-1 text-warning">
                {changed} of them {changed === 1 ? 'has' : 'have'} been scanned, edited or changed by a later import since.
              </p>
            ) : null}
          </div>
        ) : null}
        {history ? (
          <div>
            <p className="font-medium">{n > 0 ? 'And the history entry' : 'What is removed'}</p>
            <p className="text-muted-foreground">
              {runName} leaves Import History. {n > 0 ? '' : 'Its moulds stay in the catalog exactly as they are. '}The
              activity log keeps a note of the removal.
            </p>
          </div>
        ) : null}
        <div>
          <p className="font-medium">What stays</p>
          <ul className="list-disc pl-5 text-muted-foreground">
            {inCatalog > n ? <li>The other {moulds(inCatalog - n)} of this import.</li> : null}
            {!history ? <li>This import's entry in Import History.</li> : null}
            <li>Every mould that is not in this import.</li>
            <li>The spreadsheet itself: MouldTrack does not keep a copy, so there is no file to delete.</li>
          </ul>
        </div>
        {n > 0 ? (
          <label className="flex items-start gap-2 rounded-lg bg-danger-subtle px-3 py-2 text-danger">
            <input type="checkbox" className="mt-0.5 size-4" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
            <span>I understand {moulds(n)} will be deleted from the catalog.</span>
          </label>
        ) : null}
        {others.length ? (
          <label className="flex items-start gap-2 rounded-lg bg-danger-subtle px-3 py-2 text-danger">
            <input type="checkbox" className="mt-0.5 size-4" checked={othersToo} onChange={(e) => setOthersToo(e.target.checked)} />
            <span>
              I also want to delete the {moulds(others.length)} that this import did not add - for everyone, not only
              from this import.
            </span>
          </label>
        ) : null}
        {mode !== 'history' ? (
          <label className="flex items-start gap-2 text-muted-foreground">
            <input type="checkbox" className="mt-0.5 size-4" checked={history} onChange={(e) => setHistory(e.target.checked)} />
            <span>Also remove {runName} from Import History</span>
          </label>
        ) : null}
        {error ? (
          <p role="alert" className="text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
